import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { PartialPick } from '@@types/CommonTypes';
import {
    AssistantNewReportConversation,
    ReporterNewReportConversation,
} from '@@types/models/CreateReport';

type MessageType =
    | AssistantNewReportConversation
    | ReporterNewReportConversation;

export interface ConversationStoreState {
    loading?: boolean;
    messages: MessageType[];
}

interface ConversationStore extends ConversationStoreState {
    addConversationMessage: (message: MessageType) => void;
    clearConversationMessages: () => void;
    deleteConversationMessage: () => void;
    setConversationLoading: (value: boolean) => void;
}

export const ConversationStoreDefaultState: ConversationStoreState = {
    loading: false,
    messages: [],
};

/**
 * For performance optimization, it is recommended to use `useConversationStoreShallow` instead,
 * which returns a shallow copy of the state and prevents unnecessary re-renders.
 */
export const useConversationStore = create<ConversationStore>()(
    devtools(
        (set) => ({
            ...ConversationStoreDefaultState,
            addConversationMessage: (message) => {
                set((state) => ({
                    messages: [...state.messages, message],
                }));
            },
            clearConversationMessages: () => {
                set(() => ({
                    loading: false,
                    messages: [],
                }));
            },
            deleteConversationMessage: () => {
                set((state) => ({
                    messages: state.messages.slice(0, -1),
                }));
            },
            setConversationLoading: (loading) => {
                set(() => ({
                    loading,
                }));
            },
        }),
        {
            name: 'ConversationStore',
        },
    ),
);

/**
 * A hook that returns a shallow copy of the ConversationStore state.
 * Shallow copies are used to optimize performance by preventing unnecessary re-renders in Zustand.
 *
 * @param keys - An array of keys to be included in the returned state.
 * @returns - A shallow copy of the ConversationStore state that only includes the specified keys.
 */
export const useConversationStoreShallow = <K extends keyof ConversationStore>(
    keys: K[],
): PartialPick<ConversationStore, K> =>
    useConversationStore(
        useShallow((state) =>
            keys.reduce(
                (prevObject, currentKey) => ({
                    ...prevObject,
                    [currentKey]: state[currentKey],
                }),
                {} as PartialPick<ConversationStore, K>,
            ),
        ),
    );

/**
 * A utility function to get the current state of the ConversationStore.
 * This function is particularly useful when you need to access the state outside of a React component.
 *
 * @returns - The current state of the ConversationStore.
 */
export const getConversationStore = useConversationStore.getState;

export const resetConversationStore = useConversationStore.setState(
    ConversationStoreDefaultState,
);
